/**
 * File Service API
 * Handles all API calls to the File microservice for document uploads
 */

import api from './api';
import { downloadBlob, openBlobInNewTab } from '../utils/eventAttachmentDelivery';

const FILE_BASE_URL = '/files';
const DOCUMENT_BASE_URL = '/documents';
/** Application submit flows may upload multiple large files (e.g. videos up to 100-500MB) before the API call. */
const APPLICATION_UPLOAD_TIMEOUT_MS = 600000;

// ============================================================================
// FILE UPLOAD & MANAGEMENT
// ============================================================================

/**
 * Upload a single file
 * @param {File} file - File object
 * @param {Object} metadata - File metadata (category, entityType, entityId, description, title, verified)
 * @returns {Promise} Uploaded file details
 */
export const uploadFile = (file, metadata = {}, { onUploadProgress } = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  
  if (metadata.category) formData.append('category', metadata.category);
  if (metadata.entityType) formData.append('entityType', metadata.entityType);
  if (metadata.entityId) formData.append('entityId', metadata.entityId);
  if (metadata.description) formData.append('description', metadata.description);
  if (metadata.title) formData.append('title', metadata.title);
  if (metadata.accessLevel) formData.append('accessLevel', metadata.accessLevel);
  if (metadata.verified !== undefined) formData.append('verified', metadata.verified.toString());

  return api.post(`${FILE_BASE_URL}/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: APPLICATION_UPLOAD_TIMEOUT_MS,
    ...(onUploadProgress ? { onUploadProgress } : {}),
  });
};

/**
 * Upload multiple files
 * @param {FileList|Array} files - Array of file objects
 * @param {Array|Object} metadata - Array of metadata objects (one per file) or single metadata object for all files
 * @returns {Promise} Uploaded files details
 */
export const uploadMultipleFiles = (files, metadata = {}, { onUploadProgress } = {}) => {
  const formData = new FormData();
  
  Array.from(files).forEach((file) => {
    formData.append('files', file);
  });
  
  // Handle per-file metadata or single metadata for all files
  if (Array.isArray(metadata)) {
    // Per-file metadata
    formData.append('metadata', JSON.stringify(metadata));
  } else {
    // Single metadata for all files
    if (metadata.category) formData.append('category', metadata.category);
    if (metadata.entityType) formData.append('entityType', metadata.entityType);
    if (metadata.entityId) formData.append('entityId', metadata.entityId);
    if (metadata.description) formData.append('description', metadata.description);
    if (metadata.title) formData.append('title', metadata.title);
    if (metadata.accessLevel) formData.append('accessLevel', metadata.accessLevel);
    if (metadata.verified !== undefined) formData.append('verified', metadata.verified.toString());
  }

  return api.post(`${FILE_BASE_URL}/upload-multiple`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    ...(onUploadProgress ? { onUploadProgress } : {}),
  });
};

/**
 * Download a file
 * @param {string} fileId - File ID
 * @returns {Promise} File blob
 */
export const downloadFile = (fileId) => {
  return api.get(`${FILE_BASE_URL}/download/${fileId}`, {
    responseType: 'blob',
  });
};

/**
 * Get presigned URL for file access
 * @param {string} fileId - File ID
 * @param {number} expiresIn - Expiry time in seconds (default 3600)
 * @returns {Promise} Presigned URL
 */
export const getPresignedUrl = (fileId, expiresIn = 3600) => {
  return api.get(`${FILE_BASE_URL}/presigned-url/${fileId}`, {
    params: { expiresIn },
  });
};

/** Normalize presigned URL from file-service API responses. */
export function extractPresignedUrlFromResponse(response) {
  return response?.data?.data?.presignedUrl
    || response?.data?.presignedUrl
    || response?.data?.data?.url
    || response?.data?.url
    || '';
}

/**
 * Open a file in a new tab via the file-service download proxy (no raw storage URL).
 */
export async function openFileInNewTab(fileId, { mimeType = 'application/octet-stream' } = {}) {
  const id = String(fileId || '').trim();
  if (!id) throw new Error('File ID is required');

  const response = await downloadFile(id);
  openBlobInNewTab(response.data, mimeType);
}

/**
 * Download a stored file attachment (external portal / auto-download flows).
 */
export async function downloadFileAttachment(fileId, fileName = 'document', mimeType = 'application/octet-stream') {
  const id = String(fileId || '').trim();
  if (!id) throw new Error('File ID is required');

  const response = await downloadFile(id);
  downloadBlob(response.data, fileName, mimeType);
}

/**
 * Get presigned upload URL
 * @param {Object} data - File metadata
 * @returns {Promise} Presigned upload URL
 */
export const getPresignedUploadUrl = (data) => {
  return api.post(`${FILE_BASE_URL}/presigned-upload-url`, data);
};

/**
 * List files by entity
 * @param {string} entityType - Entity type (Application, Lead, Customer, etc.)
 * @param {string} entityId - Entity ID
 * @param {Object} params - Query parameters (category, etc.)
 * @returns {Promise} Files list
 */
export const listFilesByEntity = (entityType, entityId, params = {}) => {
  return api.get(`${FILE_BASE_URL}/entity/${entityType}/${entityId}`, { params });
};

/**
 * Get file metadata
 * @param {string} fileId - File ID
 * @returns {Promise} File metadata
 */
export const getFileMetadata = (fileId) => {
  return api.get(`${FILE_BASE_URL}/${fileId}`);
};

/**
 * Verify file
 * @param {string} fileId - File ID
 * @param {Object} data - Verification notes
 * @returns {Promise} Verified file
 */
export const verifyFile = (fileId, data) => {
  return api.patch(`${FILE_BASE_URL}/${fileId}/verify`, data);
};

/**
 * Update file metadata (title, description)
 * @param {string} fileId - File ID
 * @param {Object} data - Metadata (title, description, tags)
 * @returns {Promise} Updated file
 */
export const updateFileMetadata = (fileId, data) => {
  return api.patch(`${FILE_BASE_URL}/${fileId}/metadata`, data);
};

/**
 * Unverify file
 * @param {string} fileId - File ID
 * @param {Object} data - Unverification notes
 * @returns {Promise} Unverified file
 */
export const unverifyFile = (fileId, data) => {
  return api.patch(`${FILE_BASE_URL}/${fileId}/unverify`, data);
};

/**
 * Delete file (soft delete)
 * @param {string} fileId - File ID
 * @returns {Promise} Deleted file
 */
export const deleteFile = (fileId) => {
  return api.delete(`${FILE_BASE_URL}/${fileId}`);
};

/**
 * Archive file
 * @param {string} fileId - File ID
 * @returns {Promise} Archived file
 */
export const archiveFile = (fileId) => {
  return api.patch(`${FILE_BASE_URL}/${fileId}/archive`);
};

// ============================================================================
// PUBLIC DOCUMENT ACCESS (Token-based)
// ============================================================================

/**
 * Upload file without authentication (for public forms)
 * @param {File} file - File object
 * @param {Object} metadata - File metadata (category, entityType, entityId, description, title)
 * @returns {Promise} Uploaded file details
 */
export const uploadFilePublic = (file, metadata = {}, { onUploadProgress } = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  
  if (metadata.category) formData.append('category', metadata.category);
  if (metadata.entityType) formData.append('entityType', metadata.entityType);
  if (metadata.entityId) formData.append('entityId', metadata.entityId);
  if (metadata.description) formData.append('description', metadata.description);
  if (metadata.title) formData.append('title', metadata.title);

  return api.post(`${DOCUMENT_BASE_URL}/public/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: APPLICATION_UPLOAD_TIMEOUT_MS,
    ...(onUploadProgress ? { onUploadProgress } : {}),
  });
};

/**
 * Cleanup / rollback publicly uploaded files if application submission or upload fails
 * @param {string[]} fileIds - Array of file IDs to remove
 * @returns {Promise} Cleanup response
 */
export const cleanupPublicUploads = (fileIds) => {
  if (!Array.isArray(fileIds) || fileIds.length === 0) {
    return Promise.resolve({ data: { success: true, count: 0, deletedFileIds: [] } });
  }
  return api.post(`${DOCUMENT_BASE_URL}/public/cleanup`, { fileIds });
};

/**
 * Get document by token (public access)
 * @param {string} token - Access token
 * @returns {Promise} File blob
 */
export const getDocumentByToken = (token) => {
  return api.get(`${DOCUMENT_BASE_URL}/${token}`, {
    responseType: 'blob',
  });
};

/**
 * Upload document with token (public access)
 * @param {string} token - Access token
 * @param {File} file - File object
 * @param {Object} metadata - File metadata (category, description, title, verified)
 * @returns {Promise} Uploaded file details
 */
export const uploadDocumentWithToken = (token, file, metadata = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('token', token);
  
  if (metadata.category) formData.append('category', metadata.category);
  if (metadata.description) formData.append('description', metadata.description);
  if (metadata.title) formData.append('title', metadata.title);
  if (metadata.verified !== undefined) formData.append('verified', metadata.verified.toString());

  return api.post(`${DOCUMENT_BASE_URL}/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

/**
 * Upload document to application (public access)
 * @param {string} applicationId - Application ID
 * @param {File} file - File object
 * @param {Object} metadata - File metadata (category, description, title, verified)
 * @returns {Promise} Uploaded file details
 */
export const uploadDocumentToApplication = (applicationId, file, metadata = {}, { onUploadProgress } = {}) => {
  const formData = new FormData();
  formData.append('file', file);
  
  if (metadata.category) formData.append('category', metadata.category);
  if (metadata.description) formData.append('description', metadata.description);
  if (metadata.title) formData.append('title', metadata.title);
  if (metadata.verified !== undefined) formData.append('verified', metadata.verified.toString());

  return api.post(`${DOCUMENT_BASE_URL}/public/application/${applicationId}/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: APPLICATION_UPLOAD_TIMEOUT_MS,
    ...(onUploadProgress ? { onUploadProgress } : {}),
  });
};

/**
 * Get presigned URL for an application document (public access)
 * @param {string} applicationId - Application ID
 * @param {string} fileId - File ID
 * @param {number} expiresIn - Expiry time in seconds (default 3600)
 * @returns {Promise} Presigned URL response
 */
export const getApplicationDocumentPresignedUrlPublic = (applicationId, fileId, expiresIn = 3600) => {
  return api.get(`${DOCUMENT_BASE_URL}/public/application/${applicationId}/presigned-url/${fileId}`, {
    params: { expiresIn },
  });
};

/**
 * Upload document to enrollment (public access)
 */
export const uploadDocumentToEnrollment = (enrollmentId, file, metadata = {}) => {
  const formData = new FormData();
  formData.append('file', file);

  if (metadata.category) formData.append('category', metadata.category);
  if (metadata.description) formData.append('description', metadata.description);
  if (metadata.title) formData.append('title', metadata.title);
  if (metadata.verified !== undefined) formData.append('verified', metadata.verified.toString());

  return api.post(`${DOCUMENT_BASE_URL}/public/enrollment/${enrollmentId}/upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

/**
 * Get presigned URL for an enrollment document (public access)
 */
export const getEnrollmentDocumentPresignedUrlPublic = (enrollmentId, fileId, expiresIn = 3600) => {
  return api.get(`${DOCUMENT_BASE_URL}/public/enrollment/${enrollmentId}/presigned-url/${fileId}`, {
    params: { expiresIn },
  });
};



/**
 * Verify document upload token
 * @param {string} token - Access token
 * @returns {Promise} Token verification result
 */
export const verifyDocumentToken = (token) => {
  return api.get(`${DOCUMENT_BASE_URL}/verify-token/${token}`);
};

const fileApi = {
  uploadFile,
  uploadMultipleFiles,
  downloadFile,
  openFileInNewTab,
  downloadFileAttachment,
  getPresignedUrl,
  extractPresignedUrlFromResponse,
  getPresignedUploadUrl,
  listFilesByEntity,
  getFileMetadata,
  verifyFile,
  updateFileMetadata,
  unverifyFile,
  deleteFile,
  archiveFile,
  uploadFilePublic,
  cleanupPublicUploads,
  getDocumentByToken,
  uploadDocumentWithToken,
  uploadDocumentToApplication,
  getApplicationDocumentPresignedUrlPublic,
  uploadDocumentToEnrollment,
  getEnrollmentDocumentPresignedUrlPublic,
  verifyDocumentToken,
};

export default fileApi;
